import {
  FC,
  memo,
  useCallback,
  useMemo,
  useState,
  useEffect,
  useRef,
  ChangeEvent,
} from 'react';
import { debounce } from 'lodash';
import styled from 'styled-components';

import { useScroll } from '@hooks/useScroll';
import { useAppDispatch } from '@redux/store';
import { useLazyGetGifsQuery, gifsApi } from '@redux/queries/gifs';
import theme from 'theme';
import { GIFS_QUERY_PER_PAGE } from '@utils/constants';
import { TEXT_INPUT_PLACEHOLDERS } from '@utils/constants/form';
import DefaultInput from '@components/fields/input';
import Modal from '@components/modal';
import TouchableOpacity from '@components/buttons/TouchableOpacity';
import { DefaultText } from '@components/text';
import Loader from '@components/loader';

const Container = styled.div`
  flex: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  gap: 16px;
  box-sizing: border-box;
  overflow: hidden;
`;

const SearchInput = styled(DefaultInput)``;

const GifsList = styled.div`
  flex: 1;
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  max-height: 60vh;
  overflow-y: auto;
  ${theme.scrollbar.thin}
`;

const GifWrapper = styled(TouchableOpacity)`
  width: 100%;
  cursor: pointer;
  transition: transform 0.2s ease;
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const EmptyState = styled(DefaultText)`
  width: 100%;
  text-align: center;
  color: ${({ theme }) => theme.colors.grey[400]};
`;

type Props = {
  isGifModalShown: boolean;
  hideGifModal: () => void;
  onSelectGif: (url: string) => void;
};

const GifsModal: FC<Props> = ({
  isGifModalShown,
  hideGifModal,
  onSelectGif,
}) => {
  const [search, setSearch] = useState<string>('');
  const [mayLoadMore, setMayLoadMore] = useState<boolean>(false);
  const [gifs, setGifs] = useState<{ id: string; url: string }[]>([]);
  const gifsListRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<string>('');
  const pageRef = useRef<number>(0);
  const loadingRef = useRef<boolean>(false);
  const dispatch = useAppDispatch();
  const [getGifs, { data, isLoading, isSuccess }] = useLazyGetGifsQuery();

  const resetState = useCallback(() => {
    dispatch(gifsApi.util.resetApiState());
    setSearch('');
    searchRef.current = '';
    pageRef.current = 0;
    setGifs([]);
  }, [dispatch]);

  const onClose = useCallback(() => {
    resetState();
    hideGifModal();
  }, [resetState, hideGifModal]);

  const loadGifs = useCallback(
    (isRefresh = false) => {
      if (isRefresh) {
        dispatch(gifsApi.util.resetApiState());
        pageRef.current = 0;
        setGifs([]);
      }

      getGifs({
        search: searchRef.current,
        offset: isRefresh ? 0 : pageRef.current * GIFS_QUERY_PER_PAGE,
      });
    },
    [dispatch, getGifs],
  );

  const setSearchDebounced = useMemo(
    () =>
      debounce((_text: string) => {
        searchRef.current = _text;
        loadGifs(true);
      }, 600),
    [loadGifs],
  );

  const onLoadMore = useCallback(() => {
    if (mayLoadMore && !loadingRef.current) {
      loadingRef.current = true;
      pageRef.current += 1;
      getGifs({
        search: searchRef.current,
        offset: pageRef.current * GIFS_QUERY_PER_PAGE,
      });
    }
  }, [mayLoadMore, getGifs]);

  const onSelect = useCallback(
    (selected: string) => {
      onSelectGif(selected);
      onClose();
    },
    [onSelectGif, onClose],
  );

  useScroll({
    onLoadMore,
    containerRef: gifsListRef,
    isInverted: false,
  });

  useEffect(() => {
    if (isGifModalShown) {
      loadGifs(true);
    }
  }, [isGifModalShown, loadGifs]);

  useEffect(() => {
    if (!isLoading) {
      loadingRef.current = false;
    }
  }, [isLoading]);

  useEffect(() => {
    if (isSuccess && data?.data?.length) {
      const expectedOffset = pageRef.current * GIFS_QUERY_PER_PAGE;
      if (data.pagination?.offset !== expectedOffset) {
        return;
      }

      const formattedGifs = data.data.map((item) => ({
        id: item.id,
        url: item.images.fixed_height.url,
      }));

      setGifs((prev) => {
        const all = [...prev, ...formattedGifs];
        const unique = Array.from(new Map(all.map((g) => [g.id, g])).values());
        return unique;
      });
      setMayLoadMore(formattedGifs.length === GIFS_QUERY_PER_PAGE);
    }
  }, [isSuccess, data]);

  useEffect(() => {
    return () => {
      setSearchDebounced.cancel();
    };
  }, [setSearchDebounced]);

  return (
    <Modal
      isShown={isGifModalShown}
      onClose={onClose}
      title='Choose GIF'
      isSkewed={false}
      isActionable={false}
      style={{
        height: '60vh',
        width: '70vw',
      }}
    >
      <Container>
        <SearchInput
          placeholder={TEXT_INPUT_PLACEHOLDERS.SEARCH}
          value={search}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            setSearch(event.target.value);
            setSearchDebounced(event.target.value);
          }}
          isSkewed={false}
        />

        {isLoading && !gifs.length ? (
          <Loader size={'small'} />
        ) : !gifs.length ? (
          <EmptyState>No results</EmptyState>
        ) : (
          <GifsList ref={gifsListRef}>
            {gifs.map((item) => (
              <GifWrapper
                key={item.id}
                onClick={() => onSelect(item.url)}
                role='button'
              >
                <Image src={item.url} alt='gif' />
              </GifWrapper>
            ))}
          </GifsList>
        )}
      </Container>
    </Modal>
  );
};

export default memo(GifsModal);
